import { Card } from "@/shared/ui/card";
import { useDateFormat } from "@/shared/hooks/use-date-format";
import { useLanguage } from "@/shared/hooks/use-language";
import { formatAmountScale, isRenderableAmountScale } from "@/shared/lib/amount";
import { consolidationStateOf } from "@/features/portfolio/api/position";
import { formatDateDisplay } from "@/shared/lib/date-utils";
import type { PositionSummary } from "@/features/portfolio/api/position";
import type { BookingStatus } from "@/features/portfolio/hooks/use-booking-status";
import { BookingMarker } from "./BookingMarker";
import { NAV_CURRENCIES } from "@/shared/lib/nav-currency";

const ISO_DATE_PART = /^\d{4}-\d{2}-\d{2}$/;

interface NavHeaderProps {
  summary: PositionSummary | null;
  isLoading: boolean;
  isRefreshing: boolean;
  currency: string;
  onCurrencyChange: (currency: string) => void;
  /** Whether the figures below are final — see `useBookingStatus`. Each card carries the
   *  compact marker beside its figure while they are not. */
  booking: BookingStatus;
}

export const NavHeader = ({
  summary, isLoading, isRefreshing, currency, onCurrencyChange, booking,
}: NavHeaderProps) => {
  const { t } = useLanguage();
  const { dateFormat } = useDateFormat();

  // A NAV or position count rendered before the booking check has answered once is not
  // yet final — the same rule `PositionsTable` applies to its own empty state via the
  // hook's `hasAnswered` flag. Without this, the figure renders as final immediately and
  // the marker only pops in a request round-trip later.
  const awaitsBooking = !booking.hasAnswered;
  const consolidated = summary?.consolidated_nav;
  const consolidationState = consolidationStateOf(summary);
  const isConsolidated = consolidationState === "consolidated" && consolidated != null;
  // A control for a figure that isn't on screen yet: the toggle must wait on the same
  // `awaitsBooking` gate the NAV value and the compact marker below apply, or it renders
  // live and clickable directly above a skeleton shimmer.
  const showToggle = !isLoading && !awaitsBooking && summary != null && consolidationState !== "none";
  const readableRates = (consolidated?.rates ?? []).filter(
    (rate) => isRenderableAmountScale(rate.rate_amount, rate.rate_scale),
  );
  const isFxUnavailable = consolidationState === "rate-missing";
  const isNothingPriced = consolidationState === "nothing-priced";
  const isUnrepresentable = consolidationState === "unrepresentable";

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
      <Card className="p-6">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm text-muted-foreground mb-1">{t("portfolio.nav")}</p>
          {showToggle && (
            <div
              className="flex rounded-md border border-border text-xs"
              role="group"
              aria-label={t("portfolio.navCurrency")}
              data-testid="nav-currency-toggle"
            >
              {NAV_CURRENCIES.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => onCurrencyChange(option)}
                  aria-pressed={option === currency}
                  className={`min-h-8 px-3 py-1.5 ring-offset-background transition-colors first:rounded-l-md last:rounded-r-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                    option === currency
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          )}
        </div>
        <div
          aria-live="polite"
          aria-busy={isRefreshing}
          className={`transition-opacity ${isRefreshing ? "opacity-60" : ""}`}
        >
          {isLoading || awaitsBooking ? (
            <div className="skeleton-shimmer h-8 w-32 rounded bg-muted" />
          ) : isConsolidated ? (
            <div className="space-y-1">
              <p className="text-2xl font-bold tabular-nums" data-testid="nav-value">
                {formatAmountScale(consolidated.nav_amount, consolidated.nav_scale)}{" "}
                <span className="text-base font-medium text-muted-foreground">{consolidated.currency}</span>
              </p>
              {readableRates.length > 0 && (
                <p className="text-xs text-muted-foreground tabular-nums" data-testid="nav-fx-rate">
                  {readableRates.map((rate) => {
                    const datePart = rate.reference_time?.slice(0, 10);
                    const day = datePart && ISO_DATE_PART.test(datePart)
                      ? formatDateDisplay(datePart, dateFormat)
                      : null;
                    const value = formatAmountScale(rate.rate_amount, rate.rate_scale);
                    return (
                      <span key={rate.symbol} className="block">
                        {day ? `${rate.symbol} ${value} · ${day}` : `${rate.symbol} ${value}`}
                      </span>
                    );
                  })}
                </p>
              )}
            </div>
          ) : summary && summary.navs.length > 0 ? (
            <div className="space-y-1">
              {summary.navs.map((nav) => (
                <p key={nav.currency} className="text-2xl font-bold tabular-nums" data-testid="nav-value">
                  {formatAmountScale(nav.nav_amount, nav.nav_scale)}{" "}
                  <span className="text-base font-medium text-muted-foreground">{nav.currency}</span>
                </p>
              ))}
              {isFxUnavailable && (
                <p className="text-xs text-muted-foreground" data-testid="nav-fx-unavailable">
                  {t("portfolio.navFxUnavailable")}
                </p>
              )}
              {isUnrepresentable && (
                <p className="text-xs text-muted-foreground" data-testid="nav-total-unrepresentable">
                  {t("portfolio.navTotalUnrepresentable")}
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-1">
              <p className="text-2xl font-bold text-muted-foreground" data-testid="nav-value">—</p>
              {isNothingPriced && (
                <p className="text-xs text-muted-foreground" data-testid="nav-prices-pending">
                  {t("portfolio.navPricesPending")}
                </p>
              )}
            </div>
          )}
        </div>
        {/* The compact marker carries no live region of its own — see `BookingMarker`'s
            doc comment — so nesting it here announces nothing twice. */}
        {!isLoading && !awaitsBooking && (
          <div className="mt-2">
            <BookingMarker status={booking} size="compact" placement="nav" />
          </div>
        )}
      </Card>
      <Card className="p-6">
        <p className="text-sm text-muted-foreground mb-1">{t("portfolio.positionCount")}</p>
        {isLoading || awaitsBooking ? (
          <div className="skeleton-shimmer h-8 w-16 rounded bg-muted" />
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-2xl font-bold tabular-nums" data-testid="position-count">{summary?.position_count ?? "—"}</p>
            <BookingMarker status={booking} size="compact" placement="count" />
          </div>
        )}
      </Card>
    </div>
  );
};
